import { turbopayApi } from './api';
import { CLAIMS_SORT_OPTIONS } from './domain/sort';
import { buildOnboardingQuestionnaire } from './domain/questionnaire';
import { useClaimsStore } from './store';
import { useQuizStore } from '@/stores/quiz-store';
import type { ClaimsSortId, SettlementListItem } from './types';

export interface CatalogEntry {
  items: SettlementListItem[];
  error: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
}

const EMPTY: CatalogEntry = { items: [], error: null, status: 'idle' };

const cache = new Map<string, CatalogEntry>();
const inflight = new Map<string, Promise<CatalogEntry>>();
const listeners = new Set<() => void>();

export function catalogKey(query: string, sortId: ClaimsSortId): string {
  const q = query.trim().toLowerCase();
  return q ? `q:${q}` : `sort:${sortId}`;
}

export function getCatalogEntry(key: string): CatalogEntry {
  return cache.get(key) ?? EMPTY;
}

function notify() {
  for (const listener of listeners) listener();
}

export function subscribeCatalog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Prefetch / reuse the public settlements list. Cached in memory for the session. */
export function prefetchSettlements(
  query = '',
  sortId: ClaimsSortId = 'featured',
): Promise<CatalogEntry> {
  const key = catalogKey(query, sortId);
  const existing = cache.get(key);
  if (existing?.status === 'ready') return Promise.resolve(existing);

  const pending = inflight.get(key);
  if (pending) return pending;

  cache.set(key, { items: existing?.items ?? [], error: null, status: 'loading' });
  notify();

  const promise = (async (): Promise<CatalogEntry> => {
    try {
      const q = query.trim();
      const sort = CLAIMS_SORT_OPTIONS.find((o) => o.id === sortId)?.apiSort ?? 'featured';
      const listParams = {
        popular: true,
        actionable: true,
        limit: 50,
        ...(q ? { q } : { sort }),
      };
      const res =
        !q && sortId === 'featured'
          ? await turbopayApi.listOnboardingSettlements(
              listParams,
              buildOnboardingQuestionnaire(useQuizStore.getState().answers),
            )
          : await turbopayApi.listSettlements(listParams);
      const items = res.items ?? [];
      const entry: CatalogEntry = { items, error: null, status: 'ready' };
      cache.set(key, entry);
      if (!q && sortId === 'featured') {
        useClaimsStore.getState().selectDefaults(items);
      }
      notify();
      return entry;
    } catch (err) {
      console.error('settlements fetch failed', err);
      const entry: CatalogEntry = {
        items: [],
        error: 'Could not load claims. Try again.',
        status: 'error',
      };
      cache.set(key, entry);
      notify();
      return entry;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}
